// PROA BEHAVIOR-ATTN-01B-05A: read-only portfolio observations.
// Deliberately NOT a confirmed episode-aware notification inventory yet.
import { Fragment, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { backLabel } from '../../pricingJourney/navigation';
import {
  Alert, Box, Button, Chip, Divider,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from '@mui/material';
import CustomerRelationshipWorkspace from '../components/CustomerRelationshipWorkspace';
import MarginCenterSpecializedPageHeader from '../components/MarginCenterSpecializedPageHeader';

import { getConsumptionObservations } from '../services/consumptionObservationService';
import type { ConsumptionDriver, ConsumptionObservation, ConsumptionObservedEvolution } from '../types/consumptionObservations';


import { pageFrameSx } from '../../pricingJourney/components/PageHeader';
import { SkeletonTable } from '../../pricingJourney/components/Skeletons';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';

const DRIVER_LABEL: Record<ConsumptionDriver, string> = {
  frequency_led: 'Menos días con compras',
  size_led: 'Menor cantidad por día de compra',
  combined_decline: 'Menos días de compra y menor cantidad',
  offsetting_components: 'Frecuencia y cantidad cambian en sentidos opuestos',
  undetermined: 'No se distingue un componente dominante',
};
const EVOLUTION_LABEL: Record<ConsumptionObservedEvolution, string> = {
  lower_again: 'El consumo sigue bajando',
  first_observed_below_reference: 'Caída reciente frente a su referencia',
  unchanged_below_reference: 'Consumo bajo, sin una nueva caída',
  rebounding_below_reference: 'Mejora parcial del consumo',
};
const EVOLUTION_COLOR: Record<ConsumptionObservedEvolution, string> = {
  lower_again: '#fda4af',
  first_observed_below_reference: '#fbbf24',
  unchanged_below_reference: '#cbd5e1',
  rebounding_below_reference: '#6ee7b7',
};
const format = pricingNumberFormat({ maximumFractionDigits: 2 });
const fmt = (value: number | null | undefined) => value == null ? '—' : format.format(value);
const compareWithPrevious = (value: number) =>
  value < 0 ? `${fmt(Math.abs(value))} % menos que en el periodo anterior`
    : value > 0 ? `${fmt(value)} % más que en el periodo anterior`
      : 'Misma cantidad que en el periodo anterior';
const signedUnitsPer30 = (value: number | null | undefined) => {
  if (value == null) return '—';
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${fmt(value)} uds./30 días`;
};
const ratioPercent = (value: number | null | undefined, maxValue: number) => {
  if (value == null || maxValue <= 0) return '0%';
  const raw = (Math.abs(value) / maxValue) * 100;
  return `${Math.max(8, Math.min(100, raw))}%`;
};
const date = (value: string | null | undefined) => value ? value.slice(0, 10).split('-').reverse().join('/') : '—';
const safeReturn = (value: string | null) =>
  value?.startsWith('/margin-center/') && !value.startsWith('//')
    ? value : '/margin-center/control-comercial';

// BEHAVIOR-ATTN-01B-07D: evidencia visual resumida para el cribado.
// La tabla responde por qué aparece una relación; la investigación profunda
// sigue perteneciendo a la vista cliente-producto.
function ObservationEvidence({ item, onInvestigate }: {
  item: ConsumptionObservation;
  onInvestigate: () => void;
}) {
  const referenceSize = item.baseline.purchase_days > 0
    ? item.baseline.units / item.baseline.purchase_days : null;
  const currentSize = item.current.purchase_days > 0
    ? item.current.units / item.current.purchase_days : null;
  const referenceWindowLabel = item.prior_windows.older.units <= item.prior_windows.recent.units
    ? 'Histórico 1'
    : 'Histórico 2';
  const windows = [
    {
      key: 'older',
      label: 'Histórico 1',
      range: `${date(item.window_dates.b2_start)}–${date(item.window_dates.b2_end)}`,
      units: item.prior_windows.older.units,
      purchaseDays: item.prior_windows.older.purchase_days,
      tone: '#475569',
    },
    {
      key: 'recent',
      label: 'Histórico 2',
      range: `${date(item.window_dates.b1_start)}–${date(item.window_dates.b1_end)}`,
      units: item.prior_windows.recent.units,
      purchaseDays: item.prior_windows.recent.purchase_days,
      tone: '#64748b',
    },
    {
      key: 'previous',
      label: 'Periodo anterior',
      range: `${date(item.window_dates.w1_start)}–${date(item.window_dates.w1_end)}`,
      units: item.previous.units,
      purchaseDays: item.previous.purchase_days,
      tone: '#38bdf8',
    },
    {
      key: 'current',
      label: 'Periodo reciente',
      range: `${date(item.window_dates.w0_start)}–${date(item.window_dates.w0_end)}`,
      units: item.current.units,
      purchaseDays: item.current.purchase_days,
      tone: '#fbbf24',
    },
  ];
  const maxUnits = Math.max(...windows.map((window) => window.units ?? 0), 1);
  const maxEffect = Math.max(
    Math.abs(item.frequency_effect_units_per_30_days ?? 0),
    Math.abs(item.size_effect_units_per_30_days ?? 0),
    1,
  );
  return (
    <Box sx={{ p: 1.35, bgcolor: 'rgba(15,23,42,0.48)', borderRadius: 1.5 }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} gap={1.1} sx={{ mb: 1.15 }}>
        <Box sx={{ flex: 1, p: 1, borderRadius: 1.25, border: '1px solid rgba(125,211,252,0.18)', bgcolor: 'rgba(15,23,42,0.38)' }}>
          <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 800 }}>Horizonte usado</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{item.observation_horizon_days} días por ventana</Typography>
          <Typography variant="caption" color="text.secondary">Mediana histórica: {fmt(item.historical_median_interval_days)} días</Typography>
        </Box>
        <Box sx={{ flex: 1, p: 1, borderRadius: 1.25, border: '1px solid rgba(125,211,252,0.18)', bgcolor: 'rgba(15,23,42,0.38)' }}>
          <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 800 }}>Soporte histórico</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{fmt(item.historical_completed_intervals)} intervalos completos</Typography>
          <Typography variant="caption" color="text.secondary">Primera compra observada: {date(item.first_observed_purchase_date)}</Typography>
        </Box>
        <Box sx={{ flex: 1, p: 1, borderRadius: 1.25, border: '1px solid rgba(125,211,252,0.18)', bgcolor: 'rgba(15,23,42,0.38)' }}>
          <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 800 }}>Cambio reciente</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{compareWithPrevious(item.change_vs_previous_pct)}</Typography>
          <Typography variant="caption" color="text.secondary">Comparación entre periodo anterior y reciente</Typography>
        </Box>
        <Box sx={{ flex: 1, p: 1, borderRadius: 1.25, border: '1px solid rgba(248,113,113,0.18)', bgcolor: 'rgba(15,23,42,0.38)' }}>
          <Typography variant="caption" sx={{ color: '#fda4af', fontWeight: 800 }}>Desvío frente a su referencia</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{fmt(item.delta_pct)} %</Typography>
          <Typography variant="caption" color="text.secondary">Referencia elegida: {referenceWindowLabel}</Typography>
        </Box>
      </Stack>

      <Typography variant="caption" sx={{ display: 'block', mb: 0.8, color: '#7dd3fc', fontWeight: 800 }}>
        Lectura visual de las cuatro ventanas comparadas
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 1.1,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(4, minmax(0, 1fr))',
          },
          mb: 1.25,
        }}
      >
        {windows.map((window) => (
          <Box
            key={window.key}
            sx={{
              p: 1.05,
              borderRadius: 1.3,
              border: '1px solid rgba(148,163,184,0.16)',
              bgcolor: 'rgba(15,23,42,0.34)',
            }}
          >
            <Typography variant="caption" sx={{ color: window.tone, fontWeight: 800 }}>{window.label}</Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mb: 0.85 }}>{window.range}</Typography>
            <Box sx={{ height: 8, borderRadius: 999, bgcolor: 'rgba(148,163,184,0.16)', overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: ratioPercent(window.units, maxUnits), bgcolor: window.tone, borderRadius: 999 }} />
            </Box>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 0.85 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{fmt(window.units)}</Typography>
                <Typography variant="caption" color="text.secondary">uds. en la ventana</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{fmt(window.purchaseDays)}</Typography>
                <Typography variant="caption" color="text.secondary">días con compra</Typography>
              </Box>
            </Stack>
          </Box>
        ))}
      </Box>

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={1.1}>
        <Box sx={{ flex: 1, p: 1.05, borderRadius: 1.3, border: '1px solid rgba(148,163,184,0.16)', bgcolor: 'rgba(15,23,42,0.34)' }}>
          <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 800 }}>Qué compara PROA</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mt: 0.7 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Referencia histórica elegida</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmt(item.baseline.units)} uds. · {fmt(item.baseline.purchase_days)} días</Typography>
              <Typography variant="caption" color="text.secondary">{fmt(item.baseline.units_per_30_days)} uds./30 días</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Periodo reciente</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmt(item.current.units)} uds. · {fmt(item.current.purchase_days)} días</Typography>
              <Typography variant="caption" color="text.secondary">{fmt(item.current.units_per_30_days)} uds./30 días</Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 0.9, borderColor: 'rgba(148,163,184,0.14)' }} />
          <Typography variant="caption" color="text.secondary">
            PROA toma como referencia el menor de los dos periodos históricos para no comparar solo contra un pico aislado.
          </Typography>
        </Box>

        <Box sx={{ flex: 1, p: 1.05, borderRadius: 1.3, border: '1px solid rgba(148,163,184,0.16)', bgcolor: 'rgba(15,23,42,0.34)' }}>
          <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 800 }}>Qué está pesando más en el descenso</Typography>
          <Box sx={{ mt: 0.8 }}>
            <Typography variant="caption" color="text.secondary">Frecuencia de compra</Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Box sx={{ flex: 1, height: 8, borderRadius: 999, bgcolor: 'rgba(148,163,184,0.16)', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: ratioPercent(item.frequency_effect_units_per_30_days, maxEffect), bgcolor: '#38bdf8', borderRadius: 999 }} />
              </Box>
              <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'right', fontWeight: 700 }}>{signedUnitsPer30(item.frequency_effect_units_per_30_days)}</Typography>
            </Stack>
          </Box>
          <Box sx={{ mt: 0.8 }}>
            <Typography variant="caption" color="text.secondary">Cantidad por día de compra</Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Box sx={{ flex: 1, height: 8, borderRadius: 999, bgcolor: 'rgba(148,163,184,0.16)', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: ratioPercent(item.size_effect_units_per_30_days, maxEffect), bgcolor: '#f59e0b', borderRadius: 999 }} />
              </Box>
              <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'right', fontWeight: 700 }}>{signedUnitsPer30(item.size_effect_units_per_30_days)}</Typography>
            </Stack>
          </Box>
          <Divider sx={{ my: 0.9, borderColor: 'rgba(148,163,184,0.14)' }} />
          <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1' }}>
            Tamaño medio por día de compra: {fmt(referenceSize)} → {fmt(currentSize)} uds.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            La suma de frecuencia y tamaño explica el cambio observado en uds./30 días. Esta lectura describe el patrón, no una causa comercial.
          </Typography>
        </Box>
      </Stack>

      <Button size="small" sx={{ mt: 1.1, textTransform: 'none' }} variant="outlined" onClick={onInvestigate}>
        Abrir análisis cliente-producto
      </Button>
    </Box>
  );
}

export default function MarginCenterConsumptionObservationsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // 01B-07A: consulta automática de cartera sin horizonte editable.
  const cursor = params.get('cursor') || '';
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const returnPath = safeReturn(params.get('returnTo'));
  const query = useQuery({
    queryKey: ['pricing', 'consumption', 'observations', 'auto-1.2', cursor],
    queryFn: () => getConsumptionObservations({ limit: 25, cursor: cursor || undefined }),
    retry: 1, staleTime: 30000,
  });
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [cursor]);

  const nextPage = () => {
    const nextCursor = query.data?.page.next_cursor;
    if (!nextCursor) return;
    setExpandedKey(null); setCursorHistory((current) => [...current, cursor]);
    const next = new URLSearchParams(params);
    next.set('cursor', nextCursor); setParams(next);
  };
  const previousPage = () => {
    if (!cursorHistory.length) return;
    const prior = cursorHistory[cursorHistory.length - 1];
    setExpandedKey(null); setCursorHistory((current) => current.slice(0,-1));
    const next = new URLSearchParams(params);
    if (prior) next.set('cursor', prior); else next.delete('cursor');
    setParams(next);
  };
  // Reuse the existing relationship drawer: the portfolio page stays behind
  // it with the same horizon, cursor, scroll, navigation origin and filters.
  const selectedCustomer = params.get('relationshipCustomer');
  const selectedProduct = params.get('relationshipProduct');
  const selectedObservation = query.data?.items.find(item => item.customer_id === selectedCustomer && item.product_id === selectedProduct);
  const investigate = (item: ConsumptionObservation) => {
    const next = new URLSearchParams(params);
    next.set('relationshipCustomer', item.customer_id);
    next.set('relationshipProduct', item.product_id);
    setParams(next);
  };
  const closeRelationship = () => {
    const next = new URLSearchParams(params);
    next.delete('relationshipCustomer');
    next.delete('relationshipProduct');
    setParams(next, { replace: true });
  };
  const comparison = params.get('comparison');
  const comparisonMode = comparison === 'none' || comparison === 'previous_period'
    || comparison === 'year_over_year' || comparison === 'custom'
    ? comparison : 'previous_period';
  const comparisonHasDates = Boolean(params.get('comparisonFrom') && params.get('comparisonTo'));
  const effectiveComparison = comparisonMode === 'custom' && !comparisonHasDates
    ? 'previous_period' : comparisonMode;
  const grain = params.get('grain');
  const timeGrain = grain === 'day' || grain === 'month' || grain === 'week' ? grain : 'week';
  const summary = query.data?.summary;
  // 1.3 incluye dirección temporal y garantías de corte; versiones anteriores no bastan.
  const contractReady = query.data?.version === '1.3' && query.data?.selection.horizon_mode === 'automatic_per_relationship';
  return (
    <Box sx={pageFrameSx}>
      <Stack gap={1.1}>
        <MarginCenterSpecializedPageHeader
          backLabel={backLabel(returnPath)}
          onBack={() => navigate(returnPath)}
          eyebrow="Avisos comerciales"
          title="Consumo · cambios de compra detectados"
          description="Relaciones que muestran menor consumo respecto a su referencia histórica. Observaciones pendientes de validación."
          meta={<Chip size="small" variant="outlined" label="Detección provisional" />}
        />
        {query.isPending && <SkeletonTable rows={6} />}
        {query.isError && <Alert severity="warning">La consulta de Consumo no está disponible. Comprueba que la migración SQL y el backend de esta microtanda están instalados; no presentamos un cero ficticio.</Alert>}
        {query.data && !contractReady && (
          <Alert severity="warning">Se requiere la migración 01B-07B (contrato 1.3) para mostrar la evolución temporal observada. No se presentan resultados antiguos como si fueran nuevos.</Alert>
        )}
        {query.data && contractReady && <>
          <Paper elevation={0} sx={{ p: 1.6, bgcolor:'#0c172b',border:'1px solid rgba(148,163,184,0.2)',borderRadius:2 }}>
            <Stack direction="row" gap={2} useFlexGap flexWrap="wrap" alignItems="center">
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.15,
                  width: '100%',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                  },
                }}
              >
                {[
                  {
                    label: 'Cambios detectados',
                    value: fmt(summary?.detected_count),
                    note: 'Relaciones que PROA señala para revisar ahora',
                    tone: '#38bdf8',
                  },
                  {
                    label: 'Descenso en ambos periodos',
                    value: fmt(summary?.two_window_candidate_count),
                    note: 'Ya venían por debajo antes del periodo reciente',
                    tone: '#fbbf24',
                  },
                  {
                    label: 'Relaciones comparables',
                    value: fmt(summary?.relationships_comparable),
                    note: 'Base con soporte suficiente bajo este criterio',
                    tone: '#22c55e',
                  },
                  {
                    label: 'Con compras, sin comparación válida',
                    value: fmt(summary?.limited_or_invalid_count),
                    note: 'Siguen comprando, pero hoy no son evaluables aquí',
                    tone: '#94a3b8',
                  },
                ].map((kpi) => (
                  <Box
                    key={kpi.label}
                    sx={{
                      p: 1.2,
                      borderRadius: 1.5,
                      border: '1px solid rgba(148,163,184,0.16)',
                      bgcolor: 'rgba(15,23,42,0.34)',
                      minHeight: 112,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ width: 34, height: 4, borderRadius: 999, bgcolor: kpi.tone, mb: 0.9 }} />
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                      {kpi.label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
                      {kpi.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {kpi.note}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ flex:1 }} />
              <Chip label={`Datos hasta ${date(query.data.data_cutoff_date)}`} size="small" variant="outlined" />
            </Stack>
            <Divider sx={{ my:1.2 }}/>


          </Paper>
          {query.data.status === 'empty_dataset' && <Alert severity="info">La cuenta todavía no contiene transacciones disponibles.</Alert>}
          {query.data.status === 'ok' && query.data.items.length === 0 && <Alert severity="info">No hay candidatas con la política automática experimental. No demuestra ausencia de problemas: revisa cobertura y calidad del histórico.</Alert>}
          {/* 01B-06A: inventario de cribado; orden servidor y cursor global. */}
          <TableContainer component={Paper} elevation={0} sx={{ bgcolor: '#0c172b', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 2 }}>
            <Table size="small" aria-label="Relaciones con descenso de consumo observado" sx={{ '& td, & th': { borderColor: 'rgba(148,163,184,0.12)' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Relación cliente · producto</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Qué ha cambiado</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Evolución de las compras</TableCell>
                  <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Volumen</TableCell>
                  <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Vs. referencia</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.items.map(item => {
                  const identity = `${item.customer_id}|${item.product_id}`;
                  const opened = expandedKey === identity;
                  return (
                    <Fragment key={identity}>
                      <TableRow
                        hover
                        role="button"
                        tabIndex={0}
                        aria-expanded={opened}
                        onClick={() => setExpandedKey(opened ? null : identity)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setExpandedKey(opened ? null : identity);
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          transition: 'background-color 120ms ease',
                          bgcolor: opened ? 'rgba(30,41,59,0.42)' : 'transparent',
                          '& td': { py: 1.1, verticalAlign: 'top' },
                          '&:hover': { bgcolor: 'rgba(30,41,59,0.28)' },
                        }}
                      >
                        <TableCell
                          sx={{
                            minWidth: 280,
                            pl: 1.5,
                            borderLeft: opened ? '3px solid #38bdf8' : '3px solid transparent',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="baseline" useFlexGap flexWrap="wrap">
                            <Typography variant="body1" sx={{ fontWeight: 900, color: '#e2e8f0', lineHeight: 1.15 }}>
                              {item.customer_label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#cbd5e1' }}>
                              {item.product_label}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 170 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{DRIVER_LABEL[item.driver]}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <Typography variant="body2" sx={{ color: EVOLUTION_COLOR[item.observed_evolution], fontWeight: 750 }}>
                            {EVOLUTION_LABEL[item.observed_evolution]}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            {compareWithPrevious(item.change_vs_previous_pct)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 155 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                            {fmt(item.baseline.units_per_30_days)} → {fmt(item.current.units_per_30_days)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>uds./30 días</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: '#fda4af', fontWeight: 850 }}>
                            {fmt(item.delta_pct)} %
                          </Typography>
                        </TableCell>

                      </TableRow>
                      {opened && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ p: 1.25 }}>
                            <ObservationEvidence item={item} onInvestigate={() => investigate(item)} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">Página {cursorHistory.length+1} · {fmt(query.data.page.returned_count)} casos mostrados</Typography>
            <Stack direction="row" gap={1}>
              <Button size="small" onClick={previousPage} disabled={!cursorHistory.length}>Anterior</Button>
              <Button size="small" onClick={nextPage} disabled={!query.data.page.has_more}>Siguiente</Button>
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Una carga retroactiva de transacciones puede cambiar los resultados durante la paginación. En esta primera lectura,
            reinicia desde la primera página si cambia el corte o los datos. No se puede archivar ni crear un expediente automáticamente.
          </Typography>
        </>}
        {selectedCustomer && selectedProduct && (
          <CustomerRelationshipWorkspace
            customerId={selectedCustomer}
            productId={selectedProduct}
            startDate={params.get('from') || selectedObservation?.window_dates.w0_start || null}
            endDate={params.get('to') || query.data?.data_cutoff_date || null}
            comparisonMode={effectiveComparison}
            comparisonStartDate={effectiveComparison === 'custom' ? params.get('comparisonFrom') : null}
            comparisonEndDate={effectiveComparison === 'custom' ? params.get('comparisonTo') : null}
            timeGrain={timeGrain}
            initialMode="analysis"
            onClose={closeRelationship}
          />
        )}
      </Stack>
    </Box>
  );
}
